import { EAuthenticationErrorCodes } from "@/types/auth";
import { translationStore } from "@/i18n";

interface AuthErrorInfo {
  title: string;
  message: string;
}

// Auth copy lives in auth.error.<slug>.{title,message}; email-change copy lives
// in profile_settings.email_change.<slug>. Codes that say the same thing to the
// user share a slug within their context. INVALID_PASSWORD (5020) maps to
// password_not_accepted: the backend sends it only when a NEW password is refused
// (sign-up, reset), never for a wrong one at sign-in (that is
// AUTHENTICATION_FAILED_SIGN_IN), so its copy must not say "incorrect" (NG-2015).
const errorCodeSlugs: Record<"auth" | "emailChange", Record<string, string>> = {
  auth: {
    [EAuthenticationErrorCodes.INSTANCE_NOT_CONFIGURED]: "instance_not_configured",
    [EAuthenticationErrorCodes.INVALID_EMAIL]: "invalid_email",
    [EAuthenticationErrorCodes.EMAIL_REQUIRED]: "email_required",
    [EAuthenticationErrorCodes.SIGNUP_DISABLED]: "signup_disabled",
    [EAuthenticationErrorCodes.MAGIC_LINK_LOGIN_DISABLED]: "magic_link_disabled",
    [EAuthenticationErrorCodes.PASSWORD_LOGIN_DISABLED]: "password_login_disabled",
    [EAuthenticationErrorCodes.USER_ACCOUNT_DEACTIVATED]: "account_deactivated",
    [EAuthenticationErrorCodes.INVALID_PASSWORD]: "password_not_accepted",
    [EAuthenticationErrorCodes.SMTP_NOT_CONFIGURED]: "smtp_not_configured",
    [EAuthenticationErrorCodes.USER_ALREADY_EXIST]: "account_exists",
    [EAuthenticationErrorCodes.AUTHENTICATION_FAILED_SIGN_UP]: "sign_up_failed",
    [EAuthenticationErrorCodes.REQUIRED_EMAIL_PASSWORD_SIGN_UP]: "missing_fields",
    [EAuthenticationErrorCodes.INVALID_EMAIL_SIGN_UP]: "invalid_email",
    [EAuthenticationErrorCodes.INVALID_EMAIL_MAGIC_SIGN_UP]: "invalid_email",
    [EAuthenticationErrorCodes.MAGIC_SIGN_UP_EMAIL_CODE_REQUIRED]: "code_required",
    [EAuthenticationErrorCodes.USER_DOES_NOT_EXIST]: "account_not_found",
    [EAuthenticationErrorCodes.AUTHENTICATION_FAILED_SIGN_IN]: "sign_in_failed",
    [EAuthenticationErrorCodes.REQUIRED_EMAIL_PASSWORD_SIGN_IN]: "missing_fields",
    [EAuthenticationErrorCodes.INVALID_EMAIL_SIGN_IN]: "invalid_email",
    [EAuthenticationErrorCodes.INVALID_EMAIL_MAGIC_SIGN_IN]: "invalid_email",
    [EAuthenticationErrorCodes.MAGIC_SIGN_IN_EMAIL_CODE_REQUIRED]: "code_required",
    [EAuthenticationErrorCodes.INVALID_MAGIC_CODE_SIGN_IN]: "invalid_code",
    [EAuthenticationErrorCodes.INVALID_MAGIC_CODE_SIGN_UP]: "invalid_code",
    [EAuthenticationErrorCodes.EXPIRED_MAGIC_CODE_SIGN_IN]: "code_expired",
    [EAuthenticationErrorCodes.EXPIRED_MAGIC_CODE_SIGN_UP]: "code_expired",
    [EAuthenticationErrorCodes.EMAIL_CODE_ATTEMPT_EXHAUSTED_SIGN_IN]: "attempts_exhausted",
    [EAuthenticationErrorCodes.EMAIL_CODE_ATTEMPT_EXHAUSTED_SIGN_UP]: "attempts_exhausted",
    [EAuthenticationErrorCodes.OAUTH_NOT_CONFIGURED]: "oauth_not_configured",
    [EAuthenticationErrorCodes.GOOGLE_NOT_CONFIGURED]: "google_not_configured",
    [EAuthenticationErrorCodes.GOOGLE_OAUTH_PROVIDER_ERROR]: "google_error",
    [EAuthenticationErrorCodes.INVALID_PASSWORD_TOKEN]: "invalid_reset_link",
    [EAuthenticationErrorCodes.EXPIRED_PASSWORD_TOKEN]: "reset_link_expired",
    [EAuthenticationErrorCodes.INCORRECT_OLD_PASSWORD]: "incorrect_password",
    [EAuthenticationErrorCodes.MISSING_PASSWORD]: "password_required",
    [EAuthenticationErrorCodes.INVALID_NEW_PASSWORD]: "invalid_new_password",
    [EAuthenticationErrorCodes.PASSWORD_ALREADY_SET]: "password_already_set",
    [EAuthenticationErrorCodes.RATE_LIMIT_EXCEEDED]: "rate_limited",
  },
  // Self-service email changes send symbolic codes and use profile-settings copy.
  emailChange: {
    USER_ALREADY_EXIST: "email_taken",
    EMAIL_CODE_ATTEMPTS_EXCEEDED: "attempts_exceeded",
    EMAIL_CODE_EXPIRED: "code_expired",
  },
};

/** Resolve email-change refusals with the sending or verification fallback. */
export function emailChangeErrorHandler(
  errorCode: string | undefined,
  fallback: "send_error" | "verify_error",
): string {
  const slug = errorCode ? errorCodeSlugs.emailChange[errorCode] : undefined;
  return translationStore.t(`profile_settings.email_change.${slug ?? fallback}`);
}

/** Resolved per call, so the banner reads the locale at the moment it renders. */
function errorInfo(slug: string): AuthErrorInfo {
  return {
    title: translationStore.t(`auth.error.${slug}.title`),
    message: translationStore.t(`auth.error.${slug}.message`),
  };
}

// NG-1710: the SSO redirect flow (`error_redirect` in
// apps/backend/cognota/sso/views_auth.py) sends a plain-string `?error=` slug,
// a different query param and scheme from the numeric `?error_code=` above.
// These are the only values it ever sends; an unlisted one falls back to the
// generic "unknown" copy rather than rendering a raw, untranslated key.
const ssoErrorSlugs = new Set([
  "email_required",
  "invalid_email",
  "domain_not_configured",
  "sso_not_configured",
  "sso_error",
  "oidc_provider_error",
  "saml_provider_error",
  "sso_account_not_linked",
  // NG-3557: our own signed SAML RelayState came back after its hour was up.
  "sso_sign_in_expired",
  // NG-3337: a new person whose email's domain signs in to several workspaces,
  // signing in without a workspace's sign-in link.
  "sso_use_workspace_link",
  // NG-3829: the identity provider sent no email address for the person.
  "sso_email_missing",
]);

/** NG-1710: gates whether a refused SSO login (`?error=<slug>`) renders its
 *  specific copy at all. The backend has sent this param unconditionally
 *  since the NG-1686 hotfix; the frontend has just never read it, so off
 *  reproduces that silent failure (`null`) byte-for-byte. */
export function ssoErrorHandler(errorCode: string, enabled = false): AuthErrorInfo | null {
  if (!enabled) return null;
  return errorInfo(ssoErrorSlugs.has(errorCode) ? errorCode : "unknown");
}

/** NG-1613: a locked-out sign-in says so specifically.
 *
 *  NG-1762 (FR51): a session-timeout 401 (`SESSION_EXPIRED`) gets the explicit
 *  "session expired" message. Every message is read through
 *  `translationStore.t()` at call time (NG-2581). */
export function authErrorHandler(errorCode: string): AuthErrorInfo {
  if (errorCode === EAuthenticationErrorCodes.ACCOUNT_LOCKED) {
    return errorInfo("account_locked");
  }
  if (errorCode === EAuthenticationErrorCodes.SESSION_EXPIRED) {
    return {
      title: translationStore.t("auth.session_expired_title"),
      message: translationStore.t("auth.session_expired_message"),
    };
  }
  return errorInfo(errorCodeSlugs.auth[errorCode] ?? "unknown");
}
